import { updateUser, fetchUsers, deleteUser, deleteUsers, updateUsers } from 'api';
import { useUser } from 'contexts/userContext';
import useAxios from 'hooks/useAxios';
import { checkIfError } from 'utils/helpers';
import { IUserResource, IBulkDeleteRequest, IBulkUpdateRequest, IUserResourceWithRelations } from '../../server/types';

const useUpdateUser = () => {
  const [user, setUser] = useUser();
  const [request, { loading, error }] = useAxios<IUserResource>();

  const handleUpdateUser = async (id: string, data: Partial<Omit<IUserResource, 'role'> & { role: string }>) => {
    const response = (await request(updateUser(id, data))) as IUserResourceWithRelations;

    if (checkIfError(response)) return response;

    if (user?.id === id) {
      setUser((prevUser) => ({ ...prevUser, ...response }));
    }

    return response;
  };

  return [handleUpdateUser, { loading, error: error?.data?.message }] as const;
};

const useFetchUsers = () => {
  const [request, { loading, error }] = useAxios<IUserResourceWithRelations[]>();

  const handleFetchUsers = async () => {
    const users = await request(fetchUsers());

    return users;
  };

  return [handleFetchUsers, { loading, error: error?.data?.message }] as const;
};

const useDeleteUser = () => {
  const [request, { loading, error }] = useAxios<void>();
  const handleDeleteUser = (id: string) => request(deleteUser(id));
  return [handleDeleteUser, { loading, error: error?.data?.message }] as const;
};

const useBulkDeleteUsers = () => {
  const [request, { loading, error }] = useAxios<void>();
  const handleBulkDeleteUsers = (data: IBulkDeleteRequest) => request(deleteUsers(data));
  return [handleBulkDeleteUsers, { loading, error: error?.data?.message }] as const;
};

const useBulkUpdateUsers = () => {
  const [request, { loading, error }] = useAxios<IUserResource[]>();

  const handleBulkUpdateUsers = async (data: IBulkUpdateRequest<Omit<IUserResource, 'role'> & { role: string }>) => {
    const users = await request(updateUsers(data));

    return users;
  };

  return [handleBulkUpdateUsers, { loading, error: error?.data?.message }] as const;
};

export { useUpdateUser, useFetchUsers, useDeleteUser, useBulkDeleteUsers, useBulkUpdateUsers };
